import { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { track } from '@amplitude/analytics-browser';
import {
	TextField,
	MenuItem,
	OutlinedInputProps,
	CdnIcon,
} from '@fhl-world/salmon-ui';

import { AmplitudeEvents } from '@utils/amplitudeEvents';
import {
	PaymentsRepaymentsProvidersListData,
	RepaymentProvider,
} from '../../../../types/generated/pos';
import ProviderItem from './ProviderItem';
import { AccountEventOptions } from '../../utils/eventOptions';
import { flatProviders } from '../../utils/flatProviders';

const PaymentIconWrapper = styled.div`
	display: flex;
	align-items: center;
	column-gap: 8px;
	margin: -10px 0 -16px 0;
`;

const PaymentOptionText = styled.div`
	font-size: 13px;
	line-height: 20px;
	font-weight: 500;
	color: rgba(27, 31, 59, 0.65);
`;

const OptionTypeText = styled.span`
	font-size: 13px;
	line-height: 16px;
	font-weight: 500;
	color: rgba(0, 0, 0, 0.54);
	margin: 0 16px;
`;

const filterProviderList = (
	providerList: PaymentsRepaymentsProvidersListData['providers']
) => {
	return providerList?.filter(
		(
			providerInfo
		): providerInfo is Required<
			Required<PaymentsRepaymentsProvidersListData>['providers'][number]
		> => {
			return !!providerInfo.providers && !!providerInfo.type;
		}
	);
};

type ProviderSelectProps = {
	onChange: (providerCode: string) => void;
	providerCode: string | undefined;
	validation: { amount: string; payment: string };
	eventOptions: AccountEventOptions;
	disabled?: boolean;
	providerListResponse: PaymentsRepaymentsProvidersListData | null;
};

const ProviderSelect = (props: ProviderSelectProps) => {
	const {
		onChange,
		providerCode,
		validation,
		eventOptions,
		disabled,
		providerListResponse,
	} = props;

	const providerItems: RepaymentProvider[] = useMemo(() => {
		return flatProviders(providerListResponse?.providers);
	}, [providerListResponse?.providers]);

	const filteredProviderList = useMemo(() => {
		return filterProviderList(providerListResponse?.providers);
	}, [providerListResponse?.providers]);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const inputProviderCode = event.target.value;

			onChange?.(inputProviderCode);

			providerItems.forEach((provider) => {
				if (provider.code === inputProviderCode) {
					track(AmplitudeEvents.PaymentOptionSelected_Tap, {
						payment_channel: provider.code,
						product: eventOptions,
					});
				}
			});
		},
		[onChange, providerItems, eventOptions]
	);

	return (
		<TextField
			label={providerCode ? '' : 'Payment option'}
			aria-label={'payment-option'}
			error={validation.payment.length > 0}
			helperText={validation.payment}
			select
			onBlur={() => {
				track(AmplitudeEvents.PaymentOption_Tap, { product: eventOptions });
			}}
			value={providerCode || ''}
			onChange={handleChange}
			hiddenLabel={providerCode !== null}
			variant="filled"
			fullWidth
			InputProps={{ disableUnderline: true } as Partial<OutlinedInputProps>}
			disabled={disabled || !filterProviderList}
			SelectProps={{
				renderValue: (value) => {
					const selectedProvider = providerItems.find((provider) => {
						return value === provider.code;
					});

					if (!selectedProvider) return <></>;

					return (
						<PaymentIconWrapper>
							<CdnIcon code={selectedProvider.code} width={32} height={32} />
							<div>
								<PaymentOptionText>Payment option</PaymentOptionText>
								<div>{selectedProvider.name}</div>
							</div>
						</PaymentIconWrapper>
					);
				},
			}}
		>
			{filteredProviderList?.flatMap((providerInfo, providerInfoIdx) => {
				const { providers, type } = providerInfo;

				const list = providers.map((provider, providerIdx) => {
					return (
						<MenuItem
							value={provider.code}
							key={`${type}-${provider.code}-${providerInfoIdx}-${providerIdx}`}
						>
							<ProviderItem provider={provider} />
						</MenuItem>
					);
				});

				return [
					<div key={`type-${type}`}>
						<OptionTypeText>{type}</OptionTypeText>
					</div>,
					...list,
				];
			}) || <div></div>}
		</TextField>
	);
};

export default ProviderSelect;
